import { useState } from 'react';
import { toast } from 'sonner';
import { Send, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', subject: '', body: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/contact', form);
      setDone(true);
      toast.success(t.contact.success);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="contact-success">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 mb-6">
          <CheckCircle2 size={32} strokeWidth={1.75} />
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{t.contact.success}</h1>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.contact.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground">{t.contact.subtitle}</p>

      <form onSubmit={submit} className="mt-12 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.contact.name}</label>
            <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.contact.email}</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.contact.subject}</label>
          <input type="text" required minLength={3} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input"
            className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.contact.body}</label>
          <textarea rows={6} required minLength={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="contact-body-input"
            className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none" />
        </div>
        <button type="submit" disabled={submitting} data-testid="contact-submit-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity">
          {t.contact.submit} <Send size={16} />
        </button>
      </form>
    </div>
  );
}
